// main.ts
/**
 * main.ts — Akili Brain: Entry Point
 *
 * Responsibilities:
 *   1. Inject the DOM (FAB + drawer HTML)
 *   2. Initialise particle effects
 *   3. Bind ALL DOM event listeners
 *   4. Listen for CustomEvents dispatched by renderers/handlers
 *   5. Restore previous session conversation
 *   6. Register section router plugin if available
 *   7. Expose window.AkiliBrain public API
 *
 * Self-Evolve additions (v2):
 *   - Bind workflows button → showWorkflows()
 *   - Bind privacy button   → showPersonalFacts()
 *   - Wire workflow creation form (type, presets, save, cancel)
 *   - Wire personal-facts "forget all" button
 *   - Listen for akili:delete-workflow, akili:delete-personal-fact,
 *     akili:wf-form-cancel, akili:open-document custom events
 *   - Hide workflows button for client users (modeBar already hidden)
 *
 * Gloria Wilcox Emotion Wheel (v3):
 *   - Import EmotionWheelVisualizer from ./emotion-wheel (typed module)
 *   - Listen for akili:emotion-analysis CustomEvent dispatched by handlers/chat
 *   - Inject mini badge into the last bot message bubble
 *   - Show transformation drawer when intensity >= 7
 *
 * ProactiveHub (PIL Intelligence Hub):
 *   - Import ProactiveHub from ./proactive-hub (typed module)
 *   - Instantiate inside init() after injectDOM() → stored on window.AkiliBrain.proactiveHub
 *   - Listen for akili:pil-response dispatched by handlers/chat on SSE meta/done event
 *   - Destroy hub in close() to clean up polling on SPA teardown / logout
 *
 * LAZY-LOAD REFACTOR (bundle bloat fix):
 *   avatar-3d.ts (Three.js + Loom3, ~700 kB minified) previously had five
 *   separate static import edges:
 *     main.ts            → init3DAvatar, dispose3DAvatar, resetIdleHint
 *     effects.ts         → setSoundscapeMode
 *     emotion-avatar.ts  → resetIdleHint, updateAvatarCognitiveState
 *     handlers/chat.ts   → updateAvatarCognitiveState, updateAvatarEmotion,
 *                          applyPersonalityModifiers
 *     handlers/milestone → updateAvatarEmotion
 *
 *   Every static edge forces Rollup to inline the module into the primary
 *   bundle — dynamic import() calls in the same graph are silently ignored.
 *   The main bundle swelled to 924 kB (239 kB gzipped) and Three.js executed
 *   synchronously on every page load.
 *
 *   Fix applied here and in the four other files:
 *     1. All static imports of avatar-3d removed from effects.ts,
 *        emotion-avatar.ts, handlers/chat.ts, handlers/milestone.ts, main.ts.
 *     2. Each module exposes a register*() function that accepts the real
 *        avatar functions once the lazy chunk has loaded.
 *     3. init() in this file performs `import('./avatar-3d')` after injectDOM(),
 *        then calls all register*() functions atomically before init3DAvatar().
 *     4. dispose3DAvatar is stored on a module-level ref so close() can reach it
 *        without a static import.
 *
 *   Result: Three.js + Loom3 are split into a separate chunk that only loads
 *   when the assistant drawer is first opened, reducing time-to-interactive
 *   for the host portal page.
 *
 * Load via:
 *   <script type="module" src="/js/ai/assistant-modules/main.js"></script>
 *
 * Dependencies:
 *   AkiliPortal (api-client.js) must be loaded BEFORE this module.
 *   CSS is loaded via /css/ai/assistant.css through the master CSS files.
 */

import { state, STORAGE_KEY } from './state';
import {
  dom, injectDOM,
  // Personalization Roadmap additions
  updateAvatarForPersona,
} from './dom';
import { initFabParticles, registerAvatarSoundscape } from './effects';
import {
  renderSuggestions, showWelcome, updateUnreadBadge, showDocChip, clearDocChip,
  showToast,
  // Personalization Roadmap additions
  renderThreadRecallBanner,
  renderTransparencyPopover,
  updateWidgetState,
} from './ui-renderers';
import { executeAction } from './actions';
import { startRecording, stopRecording } from './voice';
import {
  isClientUser, focusInput, updateContextBar,
  showHistory, showInsights, showChat, showWorkflows, showPersonalFacts,
  newConversation, handleSend,
  loadConversationList, loadConversation, deleteConversation,
  fetchUnreadCount, loadInsights, markAllInsightsRead, handleInsightClick,
  handleFileAttach, clearAttachedDocument, showRecentDocsGrid,
  setDraftMode, setMode,
  loadProactiveSuggestions, dismissProactiveSuggestion,
  // Self-Evolve additions
  handleCreateWorkflow, handleDeleteWorkflow,
  // Personal Facts — Option B handlers (use /api/user/memory)
  handleDeletePersonalFact, handleDeleteAllPersonalFacts,
  handleToggleMemoryEnabled, handleToggleFactPin,
  // Personalization Roadmap additions
  handleProactiveSuggestionAction,
  handlePersonaChange,
  loadPersona,
  loadNotificationSettings,
  handleNotificationSettingChange,
  startWidgetPolling,
  stopWidgetPolling,
} from './handlers';
import { initNewsBindings } from './main-news'; // Awareness Pipeline — Signal Feed
// Kili Sentient — emotion avatar + morning brief
import { resetAvatarEmotion, registerAvatarIdleCallbacks } from './emotion-avatar';
// handlers/chat avatar bridge — populated after lazy load below
import { registerAvatarCallbacks } from './handlers/chat';
import { renderMorningBrief } from './ui-renderers.js';
// Gloria Wilcox Emotion Wheel — typed module, no window global needed
import { EmotionWheelVisualizer, type EmotionAnalysis } from './emotion-wheel';
// PIL Intelligence Hub — typed module
import { ProactiveHub, type PilMeta } from './proactive-hub';

// ── LAZY-LOAD REF: dispose3DAvatar ───────────────────────────────────────────
//
// Populated inside init() after `import('./avatar-3d')` resolves.
// close() calls this ref instead of a static import, keeping avatar-3d out of
// the synchronous boot path.  Null-safe: if the avatar never loaded (e.g.
// WebGL unavailable), close() skips the call silently.

let _dispose3DAvatar:  (() => void) | null = null;
let _pauseAvatarRender: (() => void) | null = null;
let _resumeAvatarRender: (() => void) | null = null;
let _forceAvatarResize: (() => void) | null = null;

// ── LAZY-LOAD REF: resetIdleHint (main.ts direct usage in keydown handler) ───
//
// main.ts binds resetIdleHint directly in bindEvents() for the input keydown
// listener.  Previously this was a static import.  The ref is populated from
// the same lazy-load promise in init().

let _resetIdleHintRef: (() => void) | null = null;

// ── FIRST-OPEN AVATAR INIT ────────────────────────────────────────────────────
//
// RENDERING-FIX: init3DAvatar MUST be called while the drawer is visible.
//
// Why: init3DAvatar reads container.clientWidth / clientHeight to size the
// WebGL renderer.  When the drawer is closed (display:none / off-screen) those
// values are 0, so the fallback gives 80×80 — BUT the ResizeObserver attached
// in init3DAvatar fires immediately on .observe() and calls _onResize() with
// W=0/H=0 (no fallback in _onResize), collapsing the canvas to 0×0 and setting
// camera.aspect = NaN.  All subsequent renders produce nothing.
//
// Fix: defer the lazy import + init3DAvatar until the first time open() runs.
// By that point the drawer has the `open` class and the container has its real
// CSS dimensions.  forceAvatarResize() is called 50 ms later (after CSS
// transitions) to synchronise the renderer with the painted layout.

let _avatarInitialised = false;

async function _lazyInitAvatar(): Promise<void> {
  const avatarContainer = document.getElementById('ai-avatar-3d');
  if (!avatarContainer) return;

  // Clear the placeholder SVG now (drawer is open — no flash of empty container)
  avatarContainer.innerHTML = '';
  avatarContainer.classList.add('av-3d-mode');

  try {
    const avatarMod = await import('./avatar-3d');
    const {
      init3DAvatar,
      dispose3DAvatar,
      resetIdleHint,
      setSoundscapeMode,
      updateAvatarCognitiveState,
      updateAvatarEmotion,
      applyPersonalityModifiers,
      forceAvatarResize,
      pauseAvatarRender,
      resumeAvatarRender,
    } = avatarMod;

    // Store all refs — close() uses pause, open() uses resume, teardown uses dispose
    _dispose3DAvatar   = dispose3DAvatar;
    _pauseAvatarRender = pauseAvatarRender;
    _resumeAvatarRender = resumeAvatarRender;
    _forceAvatarResize = forceAvatarResize;
    _resetIdleHintRef  = resetIdleHint;

    // Wire all cross-module bridges atomically
    registerAvatarSoundscape(setSoundscapeMode);
    registerAvatarIdleCallbacks({ resetIdleHint, updateAvatarCognitiveState });
    registerAvatarCallbacks({ updateAvatarCognitiveState, updateAvatarEmotion, applyPersonalityModifiers });

    // Wire milestone handler
    try {
      const milestoneMod = await import('./handlers/milestone');
      if (typeof milestoneMod.registerAvatarEmotionCallback === 'function') {
        milestoneMod.registerAvatarEmotionCallback(updateAvatarEmotion);
      }
    } catch { /* milestone bridge optional */ }

    // Mount the 3D canvas — drawer is open, container has real CSS dimensions
    init3DAvatar(avatarContainer, '/models/kili_head.glb');

    // Force a resize after CSS transitions settle (~50 ms) so the renderer
    // matches the container's painted size even if the ResizeObserver fired
    // before the transition completed.
    setTimeout(() => forceAvatarResize(), 50);

  } catch (err) {
    console.warn('[AkiliBrain] 3D avatar init failed — restoring SVG fallback:', err);
    avatarContainer.classList.remove('av-3d-mode');
    _dispose3DAvatar  = null;
    _resetIdleHintRef = null;
  }
}

// ── Open / Close / Toggle ─────────────────────────────────────────────────────

function open(section?: string) {
  if (state.open) return;
  state.open = true;
  const backdrop = dom.backdrop;
  const drawer   = dom.drawer;
  const fab      = dom.fab;
  if (backdrop) backdrop.classList.add('open');
  if (drawer)   drawer.classList.add('open');
  if (fab)      fab.setAttribute('aria-expanded', 'true');
  document.body.style.overflow = 'hidden';

  fetchUnreadCount();
  if (!isClientUser()) loadProactiveSuggestions();

  // Kili Sentient: check for morning brief (advocates only, before 10 AM)
  if (!isClientUser()) {
    const nowHour = new Date().getHours();
    if (nowHour < 10) {
      _checkMorningBrief();
    }
  }

  // ── RENDERING-FIX: init 3D avatar on first open, pause/resume on subsequent ─
  // First open: drawer is visible, container has real CSS dimensions → init.
  // Subsequent opens: avatar is already mounted; just resume the render loop
  // and re-sync the renderer size (container may have reflowed while closed).
  // This avoids re-fetching the GLB on every open/close cycle.
  if (!_avatarInitialised) {
    _avatarInitialised = true;
    void _lazyInitAvatar();
  } else if (_resumeAvatarRender) {
    // Resume + re-sync dimensions after CSS transition (~50 ms)
    _resumeAvatarRender();
    setTimeout(() => _forceAvatarResize?.(), 50);
  }

  // [PHASE 2 & 5] Load persona + apply to DOM on every drawer open
  if (!isClientUser()) {
    loadPersona().then(() => {
      if (state.persona) updateAvatarForPersona(state.persona);
    });
  }

  // [PHASE 4] Stop widget polling while drawer is open
  stopWidgetPolling();
  updateWidgetState({ unread_messages: 0, upcoming_deadlines: 0, suggested_action: null });

  if (state.view === 'history') {
    showHistory();
  } else if (state.view === 'insights') {
    showInsights();
  } else if (state.view === 'workflows') {
    showWorkflows();
  } else if (state.view === 'personalfacts') {
    showPersonalFacts();
  } else if (state.view === 'news') {
    // Re-enter news panel — handler re-renders from cached state
    import('./handlers').then(m => m.showNews && m.showNews(state));
  } else {
    updateContextBar();
    renderSuggestions();
    const msgs = dom.msgs;
    if (msgs && msgs.children.length === 0) showWelcome();
  }

  focusInput();
  if (section) window._currentSection = section;
}

function close() {
  if (!state.open) return;
  state.open = false;
  const backdrop = dom.backdrop;
  const drawer   = dom.drawer;
  const fab      = dom.fab;
  if (backdrop) backdrop.classList.remove('open');
  if (drawer)   drawer.classList.remove('open');
  if (fab)      { fab.setAttribute('aria-expanded', 'false'); fab.focus(); }
  document.body.style.overflow = '';

  // [PHASE 4] Start widget polling when drawer closes
  startWidgetPolling();

  // Dismiss any open emotion drawer when the AI drawer closes
  EmotionWheelVisualizer.hideTransformationDrawer();

  // Pause the Three.js render loop to save GPU time while the drawer is hidden.
  // We do NOT call dispose3DAvatar() here because that removes the canvas from
  // the DOM and nulls all Three.js state — meaning the next open() would have
  // to re-fetch the GLB, re-parse materials, and re-mount the canvas, causing
  // a visible blank + loading delay on every open/close cycle.
  //
  // pauseAvatarRender() just cancels the RAF.  resumeAvatarRender() + a
  // forceAvatarResize() call in open() is all that's needed to bring it back.
  _pauseAvatarRender?.();

  // PIL — tear down polling timer when the drawer is closed.
  // proactiveHub is re-initialised on the next init() call if needed.
  window.AkiliBrain?.proactiveHub?.destroy();
}

function toggle() {
  if (state.open) close(); else open();
}

// ── Proactive panel collapse / expand wiring ──────────────────────────────────

function wireProactivePanelControls() {
  const collapseBtn = dom.psCollapse;
  const pill        = dom.psPill;
  const panel       = dom.proactivePanel;

  if (collapseBtn) {
    collapseBtn.addEventListener('click', () => {
      state.psCollapsed = true;
      if (panel) panel.classList.remove('has-suggestions');
      if (pill && state.psCount > 0) pill.classList.add('visible');
      if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(20);
    });
  }

  if (pill) {
    pill.addEventListener('click', () => {
      state.psCollapsed = false;
      pill.classList.remove('visible');
      if (state.psCount > 0 && panel) panel.classList.add('has-suggestions');
      if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(20);
    });
    pill.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pill.click(); }
    });
  }
}

// ── Workflow form wiring (Self-Evolve v2) ─────────────────────────────────────

function wireWorkflowForm() {
  const newBtn     = dom.wfNewBtn;
  const form       = dom.wfForm;
  const cancelBtn  = document.getElementById('ai-wf-cancel-btn');
  const saveBtn    = document.getElementById('ai-wf-save-btn');
  const typeSelect = document.getElementById('ai-wf-type')  as HTMLSelectElement   | null;
  const promptRow  = document.getElementById('ai-wf-prompt-row');
  const cronInput  = document.getElementById('ai-wf-cron')  as HTMLInputElement    | null;

  if (newBtn && form) {
    newBtn.addEventListener('click', () => {
      const isOpen = form.style.display !== 'none';
      form.style.display = isOpen ? 'none' : 'block';
      if (!isOpen) {
        const nameInput   = document.getElementById('ai-wf-name')   as HTMLInputElement    | null;
        const matterInput = document.getElementById('ai-wf-matter') as HTMLInputElement    | null;
        const promptInput = document.getElementById('ai-wf-prompt') as HTMLTextAreaElement | null;
        if (nameInput)   nameInput.value   = '';
        if (typeSelect)  typeSelect.value  = 'case_law_alert';
        if (cronInput)   cronInput.value   = '0 9 * * 1';
        if (matterInput) matterInput.value = '';
        if (promptInput) promptInput.value = '';
        if (promptRow)   promptRow.style.display = 'none';
        document.getElementById('ai-wf-name')?.focus();
      }
    });
  }

  if (cancelBtn && form) {
    cancelBtn.addEventListener('click', () => { form.style.display = 'none'; });
  }

  if (typeSelect && promptRow) {
    typeSelect.addEventListener('change', () => {
      promptRow.style.display = typeSelect.value === 'custom_prompt' ? 'block' : 'none';
    });
  }

  if (cronInput) {
    document.querySelectorAll('.ai-wf-preset-btn').forEach(btn => {
      const el = btn as HTMLElement;
      el.addEventListener('click', () => {
        cronInput.value = el.dataset.cron ?? '';
        el.classList.add('selected');
        setTimeout(() => el.classList.remove('selected'), 800);
      });
    });
  }

  if (saveBtn) {
    saveBtn.addEventListener('click', () => {
      const name         = ((document.getElementById('ai-wf-name')   as HTMLInputElement    | null)?.value || '').trim();
      const type         =  (document.getElementById('ai-wf-type')   as HTMLSelectElement   | null)?.value || 'case_law_alert';
      const cron         = ((document.getElementById('ai-wf-cron')   as HTMLInputElement    | null)?.value || '').trim();
      const matterId     = ((document.getElementById('ai-wf-matter') as HTMLInputElement    | null)?.value || '').trim();
      const customPrompt = ((document.getElementById('ai-wf-prompt') as HTMLTextAreaElement | null)?.value || '').trim();
      handleCreateWorkflow({
        name,
        workflow_type:  type,
        cron_schedule:  cron,
        matter_id:      matterId || undefined,
        custom_prompt:  customPrompt || undefined,
      });
    });
  }
}

// ── Personal-facts form wiring (Self-Evolve v2) ───────────────────────────────

function wirePersonalFactsControls() {
  const forgetAllBtn = document.getElementById('ai-pf-forget-all');
  if (forgetAllBtn) {
    forgetAllBtn.addEventListener('click', () => { handleDeleteAllPersonalFacts(); });
  }
}

// ── DOM event listeners ───────────────────────────────────────────────────────

function bindEvents() {
  // FAB
  dom.fab.addEventListener('click', e => {
    const target = e.target as HTMLElement | null;
    if (target?.closest('.fab-radial-menu')) return;
    toggle();
  });

  const radialMenu = document.querySelector('.fab-radial-menu');
  if (radialMenu) {
    const _openMenu  = () => radialMenu.classList.add('open');
    const _closeMenu = () => radialMenu.classList.remove('open');
    dom.fab.addEventListener('mouseenter', _openMenu);
    dom.fab.addEventListener('mouseleave', _closeMenu);
    dom.fab.addEventListener('focusin',    _openMenu);
    dom.fab.addEventListener('focusout',   _closeMenu);
    radialMenu.addEventListener('mouseenter', _openMenu);
    radialMenu.addEventListener('mouseleave', _closeMenu);
  }

  if (dom.fabRmNew)   dom.fabRmNew.addEventListener('click',   e => { e.stopPropagation(); newConversation(); open(); if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(20); });
  if (dom.fabRmHist)  dom.fabRmHist.addEventListener('click',  e => { e.stopPropagation(); open(); showHistory(); if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(20); });
  if (dom.fabRmVoice) dom.fabRmVoice.addEventListener('click', e => { e.stopPropagation(); open(); setTimeout(startRecording, 350); if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(20); });

  dom.backdrop.addEventListener('click', close);
  dom.closeBtn.addEventListener('click', close);
  dom.histBtn.addEventListener('click', showHistory);
  dom.insightsBtn.addEventListener('click', showInsights);
  dom.backBtn.addEventListener('click', showChat);
  dom.newConvBtn.addEventListener('click', newConversation);

  if (dom.workflowsBtn) {
    dom.workflowsBtn.addEventListener('click', () => {
      showWorkflows();
      if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(20);
    });
  }

  if (dom.privacyBtn) {
    dom.privacyBtn.addEventListener('click', () => {
      showPersonalFacts();
      if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(20);
    });
  }

  dom.sendBtn.addEventListener('click', handleSend);
  dom.input.addEventListener('keydown', e => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
    // Signal user re-engagement to the 3D avatar's idle/spring system.
    // Uses the module-level ref — no-op until the lazy chunk has loaded.
    _resetIdleHintRef?.();
  });
  dom.input.addEventListener('input', () => {
    dom.input.style.height = 'auto';
    dom.input.style.height = Math.min(dom.input.scrollHeight, 120) + 'px';
    const sendBtn = dom.sendBtn;
    if (sendBtn) sendBtn.disabled = state.streaming || !dom.input.value.trim();
  });

  dom.drawer.addEventListener('keydown', e => {
    if (e.key === 'Escape') close();
  });

  dom.markAllRead.addEventListener('click', markAllInsightsRead);
  dom.docChipRm.addEventListener('click', clearAttachedDocument);

  dom.modeChatBtn.addEventListener('click',  () => setMode('chat'));
  dom.modeDraftBtn.addEventListener('click', () => setMode('draft'));
  if (dom.modePrepBtn) {
    dom.modePrepBtn.addEventListener('click', () => setMode('prep'));
  }
  dom.draftType.addEventListener('change', e => {
    state.draftDocType = (e.target as HTMLSelectElement).value;
  });
  if (dom.prepType) {
    dom.prepType.addEventListener('change', e => {
      state.prepType = (e.target as HTMLSelectElement).value;
    });
    state.prepType = dom.prepType.value || 'argument';
  }

  dom.attachBtn.addEventListener('click', e => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      showRecentDocsGrid();
    } else if (state.attachedDocumentId) {
      clearAttachedDocument();
    } else {
      dom.docFile.click();
    }
  });

  dom.docFile.addEventListener('change', e => {
    const target = e.target as HTMLInputElement;
    const file   = target.files && target.files[0];
    if (!file) return;
    handleFileAttach(file);
    target.value = '';
  });

  dom.micBtn.addEventListener('click', () => {
    if (state.isRecording) stopRecording(); else startRecording();
  });

  wireProactivePanelControls();
  wireWorkflowForm();
  wirePersonalFactsControls();
}

// ── Custom event listeners (decoupled cross-module communication) ─────────────

function bindCustomEvents() {
  document.addEventListener('akili:suggestion-click', () => { handleSend(); });

  document.addEventListener('akili:switch-to-chat', () => { showChat(); });

  document.addEventListener('akili:voice-transcribed', () => {
    const sendBtn = dom.sendBtn;
    const input   = dom.input;
    if (sendBtn && input) sendBtn.disabled = state.streaming || !input.value.trim();
    setTimeout(() => handleSend(), 400);
  });

  document.addEventListener('akili:load-conversation', e => {
    loadConversation((e as CustomEvent).detail);
  });
  document.addEventListener('akili:delete-conversation', e => {
    deleteConversation((e as CustomEvent).detail);
  });

  document.addEventListener('akili:insight-click', e => {
    handleInsightClick((e as CustomEvent).detail);
  });

  document.addEventListener('akili:ps-action', async e => {
    const { id, action } = (e as CustomEvent).detail;
    if (action) {
      try {
        await handleProactiveSuggestionAction(action);
      } catch (err) {
        console.warn('[Proactive] Action failed:', err);
      }
    }
    dismissProactiveSuggestion(id);
    if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(20);
  });
  document.addEventListener('akili:ps-dismiss', e => {
    dismissProactiveSuggestion((e as CustomEvent).detail);
    if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(20);
  });

  document.addEventListener('akili:attach-doc', e => {
    const { id, name } = (e as CustomEvent).detail;
    window.AkiliBrain.attachDocument(id, name);
  });

  document.addEventListener('akili:delete-workflow', e => {
    handleDeleteWorkflow((e as CustomEvent).detail);
  });

  document.addEventListener('akili:wf-form-cancel', () => {
    const form = dom.wfForm;
    if (form) form.style.display = 'none';
  });

  document.addEventListener('akili:delete-personal-fact', e => {
    handleDeletePersonalFact((e as CustomEvent).detail);
  });

  document.addEventListener('akili:toggle-fact-pin', e => {
    handleToggleFactPin((e as CustomEvent).detail.id, (e as CustomEvent).detail.pinned);
  });

  document.addEventListener('akili:toggle-memory', e => {
    handleToggleMemoryEnabled((e as CustomEvent).detail);
  });

  document.addEventListener('akili:reset-memory', () => {
    handleDeleteAllPersonalFacts();
  });

  // [PHASE 2 & 5] Persona card selection
  document.addEventListener('click', e => {
    const target = e.target as HTMLElement | null;
    const card   = target?.closest('.ai-persona-card') as HTMLElement | null;
    if (!card || !card.dataset.persona) return;
    handlePersonaChange(card.dataset.persona);
    updateAvatarForPersona(card.dataset.persona);
    if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(15);
  });

  document.addEventListener('keydown', e => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const target = e.target as HTMLElement | null;
    const card   = target?.closest('.ai-persona-card') as HTMLElement | null;
    if (!card || !card.dataset.persona) return;
    e.preventDefault();
    handlePersonaChange(card.dataset.persona);
    updateAvatarForPersona(card.dataset.persona);
  });

  // [PHASE 3] Notification setting changes
  document.addEventListener('change', e => {
    const el = e.target as HTMLInputElement | HTMLSelectElement | null;
    if (!el) return;
    if (el.id === 'ai-notif-briefing-time') {
      handleNotificationSettingChange('briefing_time', (el as HTMLSelectElement).value);
    }
    if (el.id === 'ai-notif-deadline-reminders') {
      handleNotificationSettingChange('deadline_reminders', (el as HTMLInputElement).checked);
    }
    if (el.id === 'ai-notif-weekly-digest') {
      handleNotificationSettingChange('weekly_digest', (el as HTMLInputElement).checked);
    }
  });

  document.addEventListener('akili:show-privacy', () => { loadNotificationSettings(); });

  // [PHASE 4] Mini widget click → open drawer
  document.addEventListener('click', e => {
    const target = e.target as HTMLElement | null;
    if (target?.closest('#ai-mini-widget')) {
      open();
      stopWidgetPolling();
    }
  });

  // ── [GLORIA WILCOX] Emotion analysis result → inject badge + drawer ───────
  document.addEventListener('akili:emotion-analysis', e => {
    const analysis = (e as CustomEvent<EmotionAnalysis>).detail;
    if (!analysis) return;

    const lastBotMsg = dom.msgs?.querySelector<HTMLElement>('.ai-msg.ai-msg-bot:last-child');
    if (lastBotMsg) {
      EmotionWheelVisualizer.injectMiniBadge(lastBotMsg, analysis);
    }

    if (analysis.intensity >= 7) {
      const negatives: Array<EmotionAnalysis['primary']> = ['mad', 'sad', 'scared'];
      if (negatives.includes(analysis.primary)) {
        EmotionWheelVisualizer.showTransformationDrawer(analysis);
      }
    }
  });

  // ── [PIL INTELLIGENCE HUB] Chat response PIL metadata → ProactiveHub ──────
  document.addEventListener('akili:pil-response', e => {
    const pilMeta = (e as CustomEvent<PilMeta>).detail;
    if (!pilMeta) return;
    window.AkiliBrain.proactiveHub?.onChatResponse(pilMeta);
  });

  // ── [PIL] akili:open-chat — pre-fill input and optionally auto-send ───────
  document.addEventListener('akili:open-chat', e => {
    const { prompt = '', autoSend = false } = (e as CustomEvent<{ prompt: string; autoSend: boolean }>).detail ?? {};
    if (!state.open) open();
    const input   = dom.input;
    const sendBtn = dom.sendBtn;
    if (!input) return;
    input.value = prompt;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.focus();
    if (autoSend && prompt.trim() && sendBtn) {
      setTimeout(() => sendBtn.click(), 50);
    }
  });
}

// ── Kili Sentient: morning brief ──────────────────────────────────────────────

async function _checkMorningBrief(): Promise<void> {
  try {
    const token = (typeof AkiliPortal !== 'undefined' && AkiliPortal.getToken)
      ? AkiliPortal.getToken() : '';
    const res = await fetch('/api/ai/morning-brief', {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) return;
    const data = await res.json();
    if (data.brief) renderMorningBrief(data.brief);
  } catch { /* fail silently — morning brief is best-effort */ }
}

// ── Init ──────────────────────────────────────────────────────────────────────

function init() {
  if (typeof AkiliPortal === 'undefined' || !AkiliPortal.getToken || !AkiliPortal.getToken()) return;

  injectDOM();

  // ── [PIL] Insert hub container and chips into drawer shell ────────────────
  if (!document.getElementById('pil-hub-container')) {
    const msgsContainer = document.querySelector<HTMLElement>('.ai-msgs-container');
    if (msgsContainer?.parentNode) {
      const pilHub = document.createElement('div');
      pilHub.id        = 'pil-hub-container';
      pilHub.className = 'pil-hub';
      pilHub.setAttribute('role', 'region');
      pilHub.setAttribute('aria-label', 'Intelligence alerts');

      const pilChips = document.createElement('div');
      pilChips.id        = 'pil-chips';
      pilChips.className = 'pil-chips';
      pilChips.setAttribute('role', 'group');
      pilChips.setAttribute('aria-label', 'Suggested actions');

      msgsContainer.parentNode.insertBefore(pilHub,   msgsContainer);
      msgsContainer.parentNode.insertBefore(pilChips, msgsContainer);
    }
  }

  // ── PIL Intelligence Hub ──────────────────────────────────────────────────
  const proactiveHub = new ProactiveHub({ pollInterval: 60_000 });
  void proactiveHub.init();
  window.AkiliBrain.proactiveHub = proactiveHub;

  initFabParticles();
  bindEvents();
  bindCustomEvents();

  // ── Role-based UI restrictions ────────────────────────────────────────────
  const isClient = isClientUser();
  if (isClient) {
    const modeBar = dom.modeBar;
    if (modeBar) modeBar.style.display = 'none';
  }
  if (isClient && dom.workflowsBtn) {
    dom.workflowsBtn.style.display = 'none';
  }

  // Restore session conversation
  const savedConvId = sessionStorage.getItem(STORAGE_KEY);
  if (savedConvId) state.conversationId = savedConvId;

  // Register section router plugin (admin / advocate portals)
  if (typeof window.registerSectionPlugin === 'function') {
    window.registerSectionPlugin('*', () => {
      if (state.open && state.view === 'chat') {
        updateContextBar();
        renderSuggestions();
      }
    });
  }

  // ── Awareness Pipeline: wire Signal Feed bindings ─────────────────────────
  initNewsBindings(state);
}

// ── Public API ────────────────────────────────────────────────────────────────
//
// ⚠️  ORDERING CONSTRAINT — DO NOT MOVE THIS BLOCK BELOW THE readyState GUARD.
//
// ES module scripts always execute after the DOM is parsed, meaning
// document.readyState is never 'loading' at evaluation time. The readyState
// guard therefore calls init() SYNCHRONOUSLY on the very next line. If
// window.AkiliBrain were declared after that guard, init() would attempt to
// write to window.AkiliBrain.proactiveHub while the object was still undefined.

window.AkiliBrain = {
  open,
  close,
  toggle,
  send:            handleSend,
  newConversation,
  executeAction,

  attachDocument(documentId, fileName) {
    state.attachedDocumentId = documentId;
    state.attachedFileName   = fileName || documentId;
    showDocChip(fileName || documentId);

    state.recentDocs = state.recentDocs.filter(d => d.id !== documentId);
    state.recentDocs.unshift({ id: documentId, name: fileName || documentId });
    if (state.recentDocs.length > 10) state.recentDocs.pop();
    try { localStorage.setItem('akili_recent_docs', JSON.stringify(state.recentDocs)); } catch (_) {}

    const token = (typeof AkiliPortal !== 'undefined' && AkiliPortal.getToken)
      ? AkiliPortal.getToken() : '';
    fetch('/api/ai/documents/embed', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
      body:    JSON.stringify({ documentId, fileName }),
    }).catch(() => {});
  },

  setMatterContext(matterId, matterTitle) {
    state.currentMatterId = matterId || null;
    const ctxText = dom.ctxText;
    if (ctxText && matterTitle) ctxText.textContent = 'Matter: ' + matterTitle;
  },

  clearDocument:   clearAttachedDocument,
  startVoice:      startRecording,
  stopVoice:       stopRecording,
  showWorkflows,
  showPersonalFacts,

  showNews(state) {
    import('./handlers').then(m => m.showNews && m.showNews(state));
  },

  setEmpathyDetect(on) {
    state.empathyDetect = !!on;
  },

  // Seeded as null; init() assigns the live ProactiveHub instance once DOM is ready.
  proactiveHub: null,
};

// ── Invoke init ───────────────────────────────────────────────────────────────

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
